import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Check, CircleDashed, Square, Rectangle, Phone, NotePencil, Users, CaretRight } from "@phosphor-icons/react";
import { format } from "date-fns";

const STATUS_COLORS = {
  available: "#81B29A",
  occupied: "#E07A5F",
  reserved: "#F4A261",
  blocked: "#9CA3AF",
};

const RES_STATUS_COLORS = {
  confirmed: "#2F4F4F",
  seated: "#81B29A",
  completed: "#57534E",
  cancelled: "#9CA3AF",
};

const TIER_CONFIG = {
  gold: { label: "Gold", bg: "#D4AF3720", color: "#B8860B", border: "#D4AF37" },
  silver: { label: "Silver", bg: "#C0C0C020", color: "#6B7280", border: "#A8A8A8" },
  bronze: { label: "Bronze", bg: "#CD7F3220", color: "#A0522D", border: "#CD7F32" },
};
const TierBadge = ({ tier }) => {
  if (!tier || !TIER_CONFIG[tier]) return null;
  const c = TIER_CONFIG[tier];
  return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border" style={{ backgroundColor: c.bg, color: c.color, borderColor: c.border }} data-testid={`tier-badge-${tier}`}>{c.label}</span>;
};

const ShapeIcon = ({ shape }) => shape === "round" ? <CircleDashed size={14} /> : shape === "rectangle" ? <Rectangle size={14} /> : <Square size={14} />;

const to12h = (t) => t || "";

export default function ServerDashboardPage() {
  const { user } = useAuth();
  const [myServer, setMyServer] = useState(null);
  const [myTables, setMyTables] = useState([]);
  const [mySections, setMySections] = useState([]);
  const [myReservations, setMyReservations] = useState([]);
  const [tiers, setTiers] = useState({});
  const [selectedRes, setSelectedRes] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [memberAnalytics, setMemberAnalytics] = useState(null);
  const [tableNotes, setTableNotes] = useState({});
  const [newNoteText, setNewNoteText] = useState("");
  const [noteTableId, setNoteTableId] = useState(null);
  const today = format(new Date(), "yyyy-MM-dd");

  const fetchMyServer = useCallback(async () => {
    try {
      const { data } = await api.get("/servers/me");
      setMyServer(data);
    } catch { /* silent */ }
  }, []);

  const fetchData = useCallback(async () => {
    if (!myServer) return;
    try {
      // Get sections assigned to this server
      const secRes = await api.get(`/sections?server_id=${myServer.id}`);
      const sections = secRes.data || [];
      setMySections(sections);
      const sectionIds = new Set(sections.map((s) => s.id));

      // Get all tables, filter to my sections
      const allTablesRes = await api.get("/tables/all");
      const allTables = allTablesRes.data || [];
      const filtered = allTables.filter((t) => sectionIds.has(t.section_id));
      setMyTables(filtered);

      // Get today's reservations
      const resRes = await api.get(`/reservations?date=${today}`);
      const allRes = resRes.data || [];
      // Filter to reservations assigned to my tables
      const myTableIds = new Set(filtered.map((t) => t.id));
      const myRes = allRes.filter((r) => {
        const ids = r.table_ids || [];
        if (r.table_id && !ids.includes(r.table_id)) ids.push(r.table_id);
        return ids.some((id) => myTableIds.has(id));
      });
      setMyReservations(myRes);

      // Fetch tiers
      const tierRes = await api.get(`/reservations/tiers?date=${today}`);
      setTiers(tierRes.data || {});

      // Fetch notes for my tables
      const notesRes = await api.get("/table-notes");
      const notesByTable = {};
      (notesRes.data || []).forEach((n) => {
        if (!notesByTable[n.table_id]) notesByTable[n.table_id] = [];
        notesByTable[n.table_id].push(n);
      });
      setTableNotes(notesByTable);
    } catch { /* silent */ }
  }, [myServer, today]);

  useEffect(() => { fetchMyServer(); }, [fetchMyServer]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // WebSocket for real-time updates
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");
    if (!wsUrl) return;
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/api/ws`);
      ws.onmessage = () => { fetchData(); };
      ws.onclose = () => setTimeout(() => {}, 3000);
    } catch { /* silent */ }
    return () => ws?.close();
  }, [fetchData]);

  const changeTableStatus = async (tableId, status) => {
    try {
      await api.put(`/tables/${tableId}`, { status });
      fetchData();
      toast.success(`Table ${status}`);
    } catch { toast.error("Failed"); }
  };

  const changeResStatus = async (resId, status) => {
    try {
      await api.put(`/reservations/${resId}`, { status });
      fetchData();
      toast.success(`${status}`);
    } catch { toast.error("Failed"); }
  };

  const openResDetail = async (res) => {
    setSelectedRes(res);
    setMemberAnalytics(null);
    setShowDetail(true);
    // Try to load member analytics
    if (res.guest_name || res.member_number) {
      try {
        const search = res.member_number || res.guest_name;
        const { data } = await api.get(`/members?search=${encodeURIComponent(search)}`);
        if (data.length > 0) {
          const memberRes = await api.get(`/members/${data[0].id}/analytics`);
          setMemberAnalytics(memberRes.data);
        }
      } catch { /* silent */ }
    }
  };

  if (!myServer) {
    return (
      <div className="p-6 text-center" data-testid="server-dashboard-no-server">
        <div className="max-w-md mx-auto mt-16">
          <Users size={48} className="mx-auto mb-3 text-stone-300" weight="duotone" />
          <h2 className="text-lg font-semibold text-stone-700" style={{ fontFamily: "Work Sans" }}>Welcome, {user?.name}</h2>
          <p className="text-sm text-stone-500 mt-2">Your server profile is being set up. Please ask your manager to assign you to a section.</p>
        </div>
      </div>
    );
  }

  const preServiceRes = myReservations.filter((r) => r.status === "confirmed");
  const activeRes = myReservations.filter((r) => r.status === "seated");
  const completedRes = myReservations.filter((r) => r.status === "completed");

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="server-dashboard">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: myServer.color }}>
          {myServer.name.charAt(0)}
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>
            {myServer.name}'s Section
          </h2>
          <p className="text-xs text-stone-500">{format(new Date(), "EEEE, MMMM d, yyyy")} · {mySections.length} section{mySections.length !== 1 ? "s" : ""} · {myTables.length} table{myTables.length !== 1 ? "s" : ""}</p>
        </div>
      </div>

      {/* My Tables Grid */}
      <div className="mb-6">
        <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">My Tables</h3>
        {myTables.length === 0 ? (
          <p className="text-sm text-stone-500">No tables assigned to your section yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {myTables.map((t) => {
              const tableRes = myReservations.find((r) => {
                const ids = r.table_ids || [];
                return (ids.includes(t.id) || r.table_id === t.id) && (r.status === "seated" || r.status === "confirmed");
              });
              return (
                <div key={t.id} className="bg-white rounded-lg border border-stone-200 p-3 transition-all hover:shadow-md" data-testid={`server-table-${t.name}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: STATUS_COLORS[t.status] + "30" }}>
                        <ShapeIcon shape={t.shape} />
                      </div>
                      <span className="font-semibold text-sm">{t.name}</span>
                    </div>
                    <Badge style={{ backgroundColor: STATUS_COLORS[t.status] + "20", color: STATUS_COLORS[t.status], border: "none" }} className="text-[9px]">
                      {t.status}
                    </Badge>
                  </div>
                  <p className="text-[10px] text-stone-500 mb-2">{t.seats} seats</p>
                  {tableRes && (
                    <button onClick={() => openResDetail(tableRes)} className="w-full text-left p-2 rounded-lg bg-stone-50 border border-stone-100 hover:bg-stone-100 transition-colors" data-testid={`table-res-${t.name}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-stone-800">{tableRes.guest_name}</span>
                        <TierBadge tier={tiers[tableRes.guest_name]} />
                      </div>
                      <p className="text-[10px] text-stone-500">{to12h(tableRes.time)} · Party of {tableRes.party_size}</p>
                    </button>
                  )}
                  {/* Table Notes */}
                  {(tableNotes[t.id] || []).length > 0 && (
                    <div className="mb-2 space-y-1">
                      {(tableNotes[t.id] || []).map((n) => (
                        <div key={n.id} className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-1 rounded-md">
                          {n.text}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Quick status buttons */}
                  <div className="flex gap-1 mt-2">
                    {t.status === "occupied" && (
                      <Button variant="outline" size="sm" className="text-[10px] flex-1 h-7" onClick={() => changeTableStatus(t.id, "available")} data-testid={`clear-table-${t.name}`}>
                        Clear
                      </Button>
                    )}
                    {t.status === "available" && (
                      <Button variant="outline" size="sm" className="text-[10px] flex-1 h-7" onClick={() => changeTableStatus(t.id, "occupied")} data-testid={`occupy-table-${t.name}`}>
                        Occupy
                      </Button>
                    )}
                    {t.status === "reserved" && tableRes && tableRes.status === "confirmed" && (
                      <Button variant="outline" size="sm" className="text-[10px] flex-1 h-7 text-green-700" onClick={() => changeResStatus(tableRes.id, "seated")} data-testid={`seat-table-${t.name}`}>
                        <Check size={12} className="mr-0.5" /> Seat
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upcoming Reservations */}
      {preServiceRes.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Upcoming ({preServiceRes.length})</h3>
          <div className="space-y-2">
            {preServiceRes.map((res) => (
              <button key={res.id} onClick={() => openResDetail(res)} className="w-full text-left bg-white rounded-lg border border-stone-200 p-3 flex items-center gap-3 hover:shadow-md transition-all" data-testid={`upcoming-res-${res.id}`}>
                <div className="w-10 text-center flex-shrink-0">
                  <span className="text-sm font-bold" style={{ color: "#2F4F4F" }}>{to12h(res.time)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-sm">{res.guest_name}</span>
                    {res.member_number && <Badge variant="outline" className="text-[9px]">#{res.member_number}</Badge>}
                    <TierBadge tier={tiers[res.guest_name]} />
                    <Badge className="text-[9px]" style={{ backgroundColor: (res.type === "walk-in" ? "#E07A5F" : "#2F4F4F") + "15", color: res.type === "walk-in" ? "#E07A5F" : "#2F4F4F", border: "none" }}>
                      {res.type === "walk-in" ? "Walk-In" : "Reservation"}
                    </Badge>
                  </div>
                  <p className="text-[10px] text-stone-500 mt-0.5">Party of {res.party_size}{res.notes ? ` · ${res.notes}` : ""}</p>
                </div>
                <CaretRight size={16} className="text-stone-300" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active (Seated) */}
      {activeRes.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Seated ({activeRes.length})</h3>
          <div className="space-y-2">
            {activeRes.map((res) => (
              <button key={res.id} onClick={() => openResDetail(res)} className="w-full text-left bg-white rounded-lg border border-[#81B29A]/30 p-3 flex items-center gap-3 hover:shadow-md transition-all" data-testid={`active-res-${res.id}`}>
                <div className="w-10 text-center flex-shrink-0">
                  <span className="text-sm font-bold" style={{ color: "#81B29A" }}>{to12h(res.time)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-sm">{res.guest_name}</span>
                    <TierBadge tier={tiers[res.guest_name]} />
                  </div>
                  <p className="text-[10px] text-stone-500 mt-0.5">Party of {res.party_size}</p>
                </div>
                <Button variant="outline" size="sm" className="text-[10px] text-stone-600" onClick={(e) => { e.stopPropagation(); changeResStatus(res.id, "completed"); }} data-testid={`complete-active-${res.id}`}>
                  <Check size={12} className="mr-0.5" /> Complete
                </Button>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Completed */}
      {completedRes.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Completed ({completedRes.length})</h3>
          <div className="space-y-1.5 opacity-60">
            {completedRes.slice(0, 5).map((res) => (
              <div key={res.id} className="bg-white rounded-lg border border-stone-100 p-2.5 flex items-center gap-3 text-sm" data-testid={`completed-res-${res.id}`}>
                <span className="text-xs text-stone-500 w-10 text-center">{to12h(res.time)}</span>
                <span className="text-stone-600">{res.guest_name}</span>
                <span className="text-[10px] text-stone-500">Party of {res.party_size}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reservation Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent data-testid="res-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedRes?.guest_name}
              <TierBadge tier={tiers[selectedRes?.guest_name]} />
            </DialogTitle>
          </DialogHeader>
          {selectedRes && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-stone-500">Type</Label>
                  <p className="font-medium capitalize">{selectedRes.type === "walk-in" ? "Walk-In" : "Reservation"}</p>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-stone-500">Status</Label>
                  <Badge style={{ backgroundColor: RES_STATUS_COLORS[selectedRes.status] + "20", color: RES_STATUS_COLORS[selectedRes.status], border: "none" }}>
                    {selectedRes.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-stone-500">Time</Label>
                  <p className="font-medium">{to12h(selectedRes.time)}</p>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-stone-500">Party Size</Label>
                  <p className="font-medium">{selectedRes.party_size}</p>
                </div>
                {selectedRes.member_number && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">Member #</Label>
                    <p className="font-medium">{selectedRes.member_number}</p>
                  </div>
                )}
                {selectedRes.guest_phone && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">Phone</Label>
                    <p className="font-medium">{selectedRes.guest_phone}</p>
                  </div>
                )}
              </div>
              {selectedRes.notes && (
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-stone-500">Special Requests</Label>
                  <p className="text-sm mt-1 p-2.5 rounded-lg bg-amber-50 border border-amber-100 text-amber-800">{selectedRes.notes}</p>
                </div>
              )}
              {/* Table Assignment */}
              {(() => {
                const ids = selectedRes.table_ids || [];
                if (selectedRes.table_id && !ids.includes(selectedRes.table_id)) ids.push(selectedRes.table_id);
                if (ids.length === 0) return null;
                const tableNames = ids.map((id) => myTables.find((t) => t.id === id)?.name).filter(Boolean);
                return tableNames.length > 0 ? (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">Table</Label>
                    <p className="font-medium text-sm">{tableNames.join(" + ")}</p>
                  </div>
                ) : null;
              })()}

              {/* Member Analytics */}
              {memberAnalytics && (
                <div className="pt-3 border-t border-stone-100">
                  <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-2 block">Member Analytics</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2.5 rounded-lg bg-stone-50 text-center">
                      <p className="text-[10px] text-stone-500">Total Visits</p>
                      <p className="text-lg font-bold" style={{ color: "#2F4F4F" }}>{memberAnalytics.total_visits}</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-stone-50 text-center">
                      <p className="text-[10px] text-stone-500">Last 7 Days</p>
                      <p className="text-lg font-bold" style={{ color: "#E07A5F" }}>{memberAnalytics.visits_7d}</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-stone-50 text-center">
                      <p className="text-[10px] text-stone-500">Last 30 Days</p>
                      <p className="text-lg font-bold" style={{ color: "#81B29A" }}>{memberAnalytics.visits_30d}</p>
                    </div>
                  </div>
                  {memberAnalytics.allergens && (
                    <div className="mt-2 p-2 rounded-lg bg-red-50 border border-red-100">
                      <p className="text-[10px] font-semibold text-red-600 uppercase">Allergens</p>
                      <p className="text-sm text-red-700">{memberAnalytics.allergens}</p>
                    </div>
                  )}
                  {memberAnalytics.special_requests && (
                    <p className="text-[11px] text-stone-500 mt-1">Preferences: {memberAnalytics.special_requests}</p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                {selectedRes.status === "confirmed" && (
                  <Button className="flex-1" style={{ backgroundColor: "#81B29A" }} onClick={() => { changeResStatus(selectedRes.id, "seated"); setShowDetail(false); }} data-testid="detail-seat-btn">
                    <Check size={16} className="mr-1" /> Seat Guest
                  </Button>
                )}
                {selectedRes.status === "seated" && (
                  <Button className="flex-1" style={{ backgroundColor: "#2F4F4F" }} onClick={() => { changeResStatus(selectedRes.id, "completed"); setShowDetail(false); }} data-testid="detail-complete-btn">
                    <Check size={16} className="mr-1" /> Complete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
